import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Link, Redirect} from 'react-router-dom';
import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import {FaBan, FaBell, FaCalendarAlt, FaCalendarCheck, FaCheckCircle, FaLock, FaMapMarkerAlt, FaPen, FaSyncAlt, FaUserFriends, FaUsers} from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/it';
import 'moment/locale/en-gb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Button, Fade, Modal, Row} from 'react-bootstrap';
import Dialog from 'react-bootstrap-dialog';
import CommonMetaTag from './common-meta-tag';
import PartnerBooking from './partner-booking';
import {calendarMessages} from '../constants';
import {useTranslation} from 'react-i18next';
import SkyworkPlannerToolbar from './skywork-planner-toolbar';
import {
  bookingParticipantNames,
  compactCapacityLabel,
  isFullAvailableSlot,
  isMultiPlaceSlot,
  isOpenMultiPlaceSlot
} from '../util/booking-slot-capacity';

const getCurrentWeek = date => moment(date).week();

const PartnerServiceBookingCalendar = ({
  profile,
  ownedService,
  ownedServices,
  isLogged,
  ownedFieldSlots,
  ownedFieldSlot,
  eventSelected,
  isBookingModalVisible,
  hideBookingModal,
  ownedFieldBooking,
  saveBooking,
  deleteBooking,
  ownedFieldBookings,
  showMessage,
  bookingSlots,
  bookingMultiSlots,
  getSlotsByWeek
}) => {
  const {t, i18n} = useTranslation();
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeek(new Date()));
  const [view, setView] = useState(Views.DAY);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  if (!isLogged) {
    return (
      <Redirect to="/login"/>
    );
  }

  const getBooking = bookingId => {
    return ownedFieldBookings.find(b => b.bookingId === bookingId);
  };

  const getEventDescription = slot => {
    if (slot.state === 'reserved' || slot.state === 'unavailable') {
      return slot.description;
    }

    if (slot.bookingId) {
      const booking = getBooking(slot.bookingId);
      if (booking) {
        if (isMultiPlaceSlot(slot)) {
          const participantNames = bookingParticipantNames(booking);
          return participantNames.length > 0
            ? participantNames.join(', ')
            : booking.description || '';
        }

        return `${booking.otherPlayersPayment[0] ? t('PAID') : t('TO_PAY')} ${booking.description || ''}`;
      }

      return null;
    }

    if (slot.presetPlaces > 1) {
      return t('EVENT_DESCRIPTION_PLACES', {description: slot.description, places: slot.availablePlaces})
    }

    if (slot.price > 0) {
      return t('EVENT_DESCRIPTION', {price: slot.price});
    }
    else return t('AVAILABLE');
  };

  const localizer = momentLocalizer(moment);
  const events = ownedFieldSlots.map(slot => {
    return {
      id: slot.slotId,
      title: getEventDescription(slot),
      start: new Date(slot.startDatetime),
      end: new Date(slot.endDatetime),
      slot,
      resourceId: slot.serviceId,
      booking: slot.bookingId ? getBooking(slot.bookingId) : null
    };
  });

  const filteredServices = ownedService ? ownedServices.filter(s => s.id === ownedService.id) : ownedServices;

  const resourceMap = filteredServices.map(s => {
    return {
      resourceId: s.id,
      resourceTitle: (
        <div className="skywork-planner-field-button skywork-planner-field-button--static">
          <FaMapMarkerAlt aria-hidden="true"/>
          <span className="skywork-planner-field-heading"><strong>{s.name}</strong></span>
        </div>
      )
    };
  });

  const service = serviceId => ownedServices.find(s => s.id === serviceId);

  let dialog = null;
  const onRemove = id => () => {
    dialog.show({
      title: t('CONFIRM'),
      body: t('DELETE_BOOKING_CONFIRM'),
      actions: [
        Dialog.Action(
          t('YES'),
          () => deleteBooking(ownedService ? ownedService.id : null, id),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('NO'),
          null,
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const minTime = new Date();
  minTime.setHours(7, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(23, 58, 59);

  const eventStyleGetter = event => {
    let backgroundColor = event.slot.state === 'reserved' ? event.slot.color ? event.slot.color : '#ADB5BD' : event.slot.state === "unavailable" ? '#444444' : isFullAvailableSlot(event.slot) ? '#3598DB' : '#31BC8C';

    if (event.slot.state === 'available' && event.slot.availablePlaces > 0) {
      backgroundColor = '#31BC8C';
    }

    const color = '#fff';
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 1,
      color,
      border: '1px',
      borderStyle: 'solid',
      borderColor: '#00152F',
      display: 'block'
    };
    let stateClass = 'single';
    if (event.slot.state === 'reserved') stateClass = 'reserved';
    else if (event.slot.state === 'unavailable') stateClass = 'unavailable';
    else if (isFullAvailableSlot(event.slot)) stateClass = 'booked';
    else if (isOpenMultiPlaceSlot(event.slot) && event.slot.minimumPlayers !== null) stateClass = 'invitation';
    else if (isOpenMultiPlaceSlot(event.slot)) stateClass = 'places';
    else if (event.slot.bookingId) stateClass = 'booked';
    const subscriptionClass = event.booking && event.booking.bookingSeriesId
      ? ' booking-calendar-slot--subscription'
      : '';
    return {className: `booking-calendar-slot booking-calendar-slot--${stateClass}${subscriptionClass}`, style};
  };

  const slotStatus = event => {
    if (event.slot.state === 'reserved') return t('BOOKING_SLOT_RESERVED');
    if (event.slot.state === 'unavailable') return t('BOOKING_SLOT_UNAVAILABLE');
    if (isOpenMultiPlaceSlot(event.slot)) return compactCapacityLabel(event.slot, t);
    if (isFullAvailableSlot(event.slot)) return t('BOOKED');
    if (event.slot.bookingId) return t('BOOKED');
    if (event.slot.minimumPlayers !== null) return t('BOOKING_SLOT_INVITATION');
    if (event.slot.presetPlaces > 1) return t('BOOKING_SLOT_PLACES');
    return t('AVAILABLE');
  };

  const SlotIcon = ({event}) => {
    if (event.slot.state === 'reserved') return <FaLock/>;
    if (event.slot.state === 'unavailable') return <FaBan/>;
    if (isOpenMultiPlaceSlot(event.slot)) return <FaUsers/>;
    if (isFullAvailableSlot(event.slot)) return <FaBell/>;
    if (event.slot.bookingId && event.booking) return <FaCheckCircle/>;
    if (event.slot.bookingId) return <FaBell/>;
    if (event.slot.minimumPlayers !== null) return <FaUserFriends/>;
    if (event.slot.presetPlaces > 1) return <FaUsers/>;
    return <FaCalendarAlt/>;
  };

  const CalendarEvent = ({event}) => (
    <div className="booking-calendar-slot-content">
      <span className="booking-calendar-slot-status"><SlotIcon event={event}/>{slotStatus(event)}</span>
      <span className="booking-calendar-slot-title">{event.title}</span>
      {event.booking && event.booking.bookingSeriesId ?
        <span className="booking-calendar-slot-subscription">
          <FaSyncAlt aria-hidden="true"/>
          {t('BOOKING_SERIES_BADGE')} {Number(event.booking.bookingSeriesOccurrence) + 1}/{event.booking.bookingSeries ? event.booking.bookingSeries.occurrences : ''}
        </span>
      : null}
    </div>
  );

  const Toolbar = toolbarProps => (
    <SkyworkPlannerToolbar
      {...toolbarProps}
      view={view}
      fields={ownedService ? [ownedService] : []}
      selectedFieldId={ownedService ? ownedService.id : ''}
      onFieldChange={ownedService ? () => {} : null}
      allFieldsLabel={null}
    />
  );

  const handleSlotMultiselection = event => {
    // Seleziono slot nel range
    const selectedSlots = ownedFieldSlots.filter(s => s.serviceId === event.resourceId && new Date(s.startDatetime) < event.end && new Date(s.endDatetime) > event.start);
    // La selezione non contiene elementi
    if (selectedSlots.length === 0) {
      showMessage('BOOKING', 'BOOKING_EMPTY_SELECTION');
      return;
    }

    // Controllo che non ci siano eventi non disponibili
    const unavailableSlots = selectedSlots.filter(s => s.state === 'unavailable');
    if (unavailableSlots.length !== 0) {
      showMessage('BOOKING', 'BOOKING_EVENT_UNAVAILABLE');
      return;
    }

    // Controllo che non ci siano eventi riservati
    const reservedSlots = selectedSlots.filter(s => s.state === 'reserved');
    if (reservedSlots.length !== 0) {
      showMessage('BOOKING', 'BOOKING_EVENT_RESERVED');
      return;
    }

    // Controllo che non ci siamo eventi con la prenotazione multipla
    if (selectedSlots.length > 1) {
      for (let i = 0; i < selectedSlots.length; i++) {
        if (selectedSlots[i].presetPlaces > 1) {
          showMessage('BOOKINGS', 'BOOKING_EVENT_NOT_ALLOWED');
          return;
        }
      }
    }

    // Controllo che non ci siano eventi prenotati
    const bookingSlots = selectedSlots.filter(s => s.bookingId);
    if (bookingSlots.length !== 0) {
      showMessage('BOOKING', 'EVENT_ALREADY_BOOKED');
      return;
    }

    // Ordino per startDate
    selectedSlots.sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime));
    // Controllo che siano adiacenti (startDate === endDate precedente)
    let endDateTarget = null;
    for (let i = 0; i < selectedSlots.length; i++) {
      if (endDateTarget !== null && endDateTarget !== selectedSlots[i].startDatetime) {
        showMessage('BOOKING', 'BOOKING_SELECTION_NOT_ADJACENT');
        return;
      }

      endDateTarget = selectedSlots[i].endDatetime;
    }

    // Ultimo controllo!! La data di inizio del primo evento non deve essere nel passato
    if (new Date(selectedSlots[0].startDatetime) < new Date()) {
      showMessage('BOOKING', 'BOOKING_EVENT_HAS_PASSED');
      return;
    }

    // La selezione è valida, posso prenotare!!
    bookingMultiSlots(selectedSlots);
  };

  const onNavigate = date => {
    const newWeek = getCurrentWeek(date);
    if (newWeek !== currentWeek) {
      setCurrentWeek(newWeek);
      getSlotsByWeek(ownedService ? ownedService.id : null)(date);
    }
  };

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="player-booking-calendar-shell partner-skywork-calendar-shell" style={{transition: 'opacity 0.30s linear', marginTop: 100, marginBottom: 100, '--current-time-label': `"${moment(currentTime).format('HH:mm')}"`}}>
        <Row className="justify-content-center player-booking-calendar-layout">
          <div className="partner-planner-heading">
            <div>
              <span>{t('PARTNER_PLANNER')}</span>
              <h4>{ownedService ? ownedService.name : profile.name} · {t('BOOKING_ADMINISTRATION')}</h4>
              <p>{ownedService ? ownedService.bookingPolicy : null}</p>
            </div>
            <div className="partner-planner-mode-switch">
              <Link to={ownedService ? "/partner-service-calendar" : "/partner-fields"}>
                <FaPen aria-hidden="true"/>
                <span>{t('PLAN')}<small>{t('CREATE_EDIT_SLOTS')}</small></span>
              </Link>
              <Link className="active" to="/partner-service-booking-calendar">
                <FaCalendarCheck aria-hidden="true"/>
                <span>{t('BOOK')}<small>{t('MANAGE_BOOKINGS')}</small></span>
              </Link>
            </div>
          </div>
          <Calendar
            selectable
            localizer={localizer}
            culture={i18n.language ===  'en' ? 'en-GB' : i18n.language}
            defaultDate={new Date()}
            defaultView={Views.DAY}
            view={view}
            onView={setView}
            views={['day', 'week']}
            min={minTime}
            max={maxTime}
            events={events}
            resources={resourceMap}
            step={15}
            timeslots={4}
            style={{minHeight: 600}}
            eventPropGetter={eventStyleGetter}
            components={{event: CalendarEvent, toolbar: Toolbar}}
            resourceIdAccessor="resourceId"
            resourceTitleAccessor="resourceTitle"
            messages={i18n.language === 'it' ? calendarMessages : null}
            onSelectEvent={eventSelected}
            onSelectSlot={handleSlotMultiselection}
            onNavigate={onNavigate}
          />
        </Row>
      </Fade>
      {
        ownedFieldSlot === null && bookingSlots === null ?
          null :
          <Modal show={isBookingModalVisible} onHide={hideBookingModal} centered dialogClassName="partner-booking-modal">
            <Modal.Header closeButton>
              <Modal.Title>{t('BOOKING')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PartnerBooking booking={ownedFieldBooking} save={saveBooking} partner={profile} slot={ownedFieldSlot} slots={bookingSlots} field={ownedFieldSlot ? service(ownedFieldSlot.serviceId) : service(bookingSlots[0].serviceId)} ownedFieldSlots={ownedFieldSlots} resourceType='service'/>
            </Modal.Body>
            <Modal.Footer>
              {/*
                ownedFieldBooking === null || !ownedFieldBooking.users || (ownedFieldSlot && ownedFieldSlot.presetPlaces > 1) || (bookingSlots && bookingSlots[0].presetPlaces > 1) || (ownedFieldSlot && ownedFieldSlot.state === 'unavailable') || (bookingSlots && bookingSlots[0].state === 'unavailable') ?
                  null :
                  <Button variant="warning" onClick={onUpload(ownedFieldSlot ? ownedFieldSlot.slotId : bookingSlots[0].slotId)}>
                    {t('VIDEO_UPLOAD')}
                  </Button>
      */}
              <Button variant="primary" onClick={hideBookingModal}>
                {t('CLOSE_BUTTON')}
              </Button>
              {
                ownedFieldBooking === null || !ownedFieldBooking.bookingId ?
                  null :
                  <Button type="button" variant="secondary" style={{marginLeft: 10}} onClick={onRemove(ownedFieldBooking.bookingId)}>{ownedFieldSlot && ownedFieldSlot.presetPlaces > 1 ? t('DELETE_ALL') : t('DELETE')}</Button>
              }
            </Modal.Footer>
          </Modal>
      }
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
    </Fragment>
  );
};

PartnerServiceBookingCalendar.propTypes = {
  isLogged: PropTypes.bool,
  profile: PropTypes.object,
  ownedService: PropTypes.object,
  ownedFieldSlots: PropTypes.array,
  ownedFieldSlot: PropTypes.object,
  ownedFieldBooking: PropTypes.object,
  isBookingModalVisible: PropTypes.bool,
  eventSelected: PropTypes.func.isRequired,
  hideBookingModal: PropTypes.func.isRequired,
  saveBooking: PropTypes.func.isRequired,
  deleteBooking: PropTypes.func.isRequired,
  ownedFieldBookings: PropTypes.array,
  ownedServices: PropTypes.array,
  showMessage: PropTypes.func.isRequired,
  bookingMultiSlots: PropTypes.func.isRequired,
  bookingSlots: PropTypes.array,
  getSlotsByWeek: PropTypes.func.isRequired
};

PartnerServiceBookingCalendar.defaultProps = {
  isLogged: false,
  ownedService: null,
  profile: null,
  ownedFieldSlots: [],
  ownedFieldSlot: null,
  ownedFieldBooking: null,
  isBookingModalVisible: false,
  ownedFieldBookings: [],
  ownedServices: [],
  bookingSlots: null
};

export default PartnerServiceBookingCalendar;
